/**
 * The two halves of "a graph, as a saved view" — kept in one module because
 * they are inverses and have to agree.
 *
 * A saved view row carries `state.extra`; a graph puts its `GraphShareState`
 * there. What must NOT go in the row is the frozen scene: it rides on the
 * serving snapshot instead, which `saved-views-api` offloads to S3. A merged
 * case scene in a DynamoDB item is how a saved view stops fitting in one (400KB),
 * and the two save paths had drifted — the views bar stripped it, the graphs
 * page sent `{ extra: { ...shown } }` scene and all. Both call this now.
 *
 * Lives beside `GraphSavedViewsBar` rather than inside it: that component is
 * lazily imported by the app-root graph defaults, and a helper exported next to
 * a lazily-loaded module is the thing that keeps getting pulled into the eager
 * chunk it was split out of.
 */
import type {
  AccessWarning,
  SavedViewServingInput,
  ViewState,
} from "@authe/saved-views";
import { buildGraphViewServing, type GraphShareState } from "@authe/tx-graph";

/** Read the graph payload + access warnings a saved view-state carries. */
export function readGraphState(state: ViewState | null | undefined): {
  graph: GraphShareState | null;
  warnings: AccessWarning[];
} {
  const extra = (state?.extra ?? null) as
    | (GraphShareState & { warnings?: AccessWarning[] })
    | null;
  return { graph: extra, warnings: extra?.warnings ?? [] };
}

/**
 * What to persist for `graph`: the row state, and the share-serving config.
 *
 * The scene stays out of the ROW and rides on the serving snapshot, which is
 * stored for a payload that size. Nodes/edges DO stay: the element-level
 * surfaces apply a view by swapping them straight back in, and they are the
 * shape `readGraphState` hands back.
 *
 * Both ways of serving the view are persisted (a frozen snapshot AND a live
 * re-fetch seed) so one saved view backs the whole share matrix and each link
 * picks static or dynamic — together with the catalog overlay the graph was
 * being read with, so a link can also pick whose tags the reader sees.
 */
export function graphViewSaveInput(graph: GraphShareState): {
  state: ViewState;
  serving: SavedViewServingInput;
} {
  // The scene and the frozen catalog both stay out of the ROW: they are sized
  // like payloads (a scene, and an overlay entry per address in it) and the
  // backend stores them beside it — the scene in S3, the overlay on the view's
  // `catalog_snapshot`, which is what an `owner_snapshot` share serves.
  const { scene: _scene, catalog: _catalog, ...rowState } = graph;
  return { state: { extra: rowState }, serving: buildGraphViewServing(graph) };
}
